import React, { createContext, useContext, useEffect, useState } from 'react';
import { Node, mergeAttributes } from '@tiptap/core';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import type { NodeViewProps } from '@tiptap/react';
import { useTheme } from '@mui/material';

import {
  SCHWACHSTELLE_REF_ATTR,
  formatFindingRef,
  missingFindingRefLabel,
  needsSpaceAfterFindingRef,
  needsSpaceBeforeFindingRef,
  type FindingReferenceOption,
} from '../../utils/schwachstelleReferenz';

export interface FindingRefCatalog {
  byId: Map<number, FindingReferenceOption>;
  lang: 'de' | 'en';
}

export const FindingRefCatalogContext = createContext<FindingRefCatalog>({
  byId: new Map(),
  lang: 'de',
});

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    schwachstelleRef: {
      /** Schwachstelle über ihre stabile ID einfügen. */
      insertSchwachstelleRef: (schwachstelleId: number) => ReturnType;
    };
  }
}

function parseId(raw: string | null): number | null {
  if (!raw || !/^\d+$/.test(raw)) return null;
  const id = Number(raw);
  return Number.isInteger(id) && id > 0 ? id : null;
}

function textBesideRef(
  editor: NodeViewProps['editor'],
  getPos: NodeViewProps['getPos'],
  nodeSize: number,
  side: 'before' | 'after',
): string {
  const pos = getPos();
  if (typeof pos !== 'number') return '';
  if (side === 'after') {
    const start = pos + nodeSize;
    return editor.state.doc.textBetween(start, Math.min(start + 1, editor.state.doc.content.size), '', '');
  }
  if (pos <= 0) return '';
  return editor.state.doc.textBetween(Math.max(0, pos - 1), pos, '', '');
}

const SchwachstelleRefView: React.FC<NodeViewProps> = ({ node, selected, editor, getPos }) => {
  const theme = useTheme();
  const catalog = useContext(FindingRefCatalogContext);
  const [, setRev] = useState(0);
  useEffect(() => {
    const bump = () => setRev((n) => n + 1);
    editor.on('transaction', bump);
    return () => {
      editor.off('transaction', bump);
    };
  }, [editor]);
  const id = Number(node.attrs.schwachstelleId);
  const target = Number.isInteger(id) ? catalog.byId.get(id) : undefined;
  const label = target
    ? formatFindingRef(target, catalog.lang)
    : missingFindingRefLabel(catalog.lang, id);
  const lead = needsSpaceBeforeFindingRef(textBesideRef(editor, getPos, node.nodeSize, 'before')) ? '\u00A0' : '';
  const gap = needsSpaceAfterFindingRef(textBesideRef(editor, getPos, node.nodeSize, 'after')) ? '\u00A0' : '';

  return (
    <NodeViewWrapper
      as="span"
      className="stark-schwachstelle-ref"
      data-schwachstelle-ref={id}
      data-testid="schwachstelle-ref"
      data-kapitel={target?.kapitel ?? ''}
      data-titel={target?.titel ?? ''}
    >
      {lead}
      <span
        style={{
          color: theme.palette.primary.main,
          fontWeight: 700,
          textDecoration: 'underline',
          textUnderlineOffset: '2px',
          outline: selected ? `2px solid ${theme.palette.primary.main}` : undefined,
          borderRadius: 2,
        }}
      >
        {label}
      </span>
      {gap}
    </NodeViewWrapper>
  );
};

/**
 * Inline-Atom: im HTML nur ``data-schwachstelle-ref="<id>"``.
 * Titel und Kapitelnummer kommen aus dem Katalog, nicht aus dem gespeicherten Text.
 */
export const SchwachstelleRef = Node.create({
  name: 'schwachstelleRef',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,
  draggable: false,

  addAttributes() {
    return {
      schwachstelleId: {
        default: null,
        parseHTML: (element) => parseId(element.getAttribute(SCHWACHSTELLE_REF_ATTR)),
        renderHTML: (attributes) => {
          const id = Number(attributes.schwachstelleId);
          if (!Number.isInteger(id) || id <= 0) return {};
          return { [SCHWACHSTELLE_REF_ATTR]: String(id) };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: `span[${SCHWACHSTELLE_REF_ATTR}]`,
        getAttrs: (element) => {
          const el = element as HTMLElement;
          const id = parseId(el.getAttribute(SCHWACHSTELLE_REF_ATTR));
          if (id == null) return false;
          return { schwachstelleId: id };
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(HTMLAttributes, { class: 'stark-schwachstelle-ref' }),
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(SchwachstelleRefView);
  },

  addCommands() {
    return {
      insertSchwachstelleRef:
        (schwachstelleId: number) =>
        ({ chain, state }) => {
          if (!Number.isInteger(schwachstelleId) || schwachstelleId <= 0) return false;
          const at = state.selection.from;
          const to = state.selection.to;
          const preceding = state.doc.textBetween(Math.max(0, at - 1), at, '', '');
          const following = state.doc.textBetween(to, Math.min(to + 1, state.doc.content.size), '', '');
          const content: Array<{ type: string; attrs?: { schwachstelleId: number }; text?: string }> = [];
          if (needsSpaceBeforeFindingRef(preceding)) content.push({ type: 'text', text: ' ' });
          content.push({ type: this.name, attrs: { schwachstelleId } });
          if (needsSpaceAfterFindingRef(following)) content.push({ type: 'text', text: ' ' });
          return chain().focus().insertContent(content).run();
        },
    };
  },
});
